/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { User, withDerivedPermissions } from '@shared/models';
import { extractHttpErrorMessage } from '@shared/utils';
import { OAuthService } from 'angular-oauth2-oidc';
import { NgxPermissionsService } from 'ngx-permissions';
import { from, Observable, of } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { clearSentryUser, setSentryUser } from '../../sentry';
import * as AuthActions from './auth.actions';

@Injectable()
export class AuthEffects {
  init = createEffect(() => this.actions$
    .pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(_ => this.oauthService.hasValidAccessToken()),
      switchMap((isAuthenticated) => {
        const payload = {
          authenticated: isAuthenticated,
          user: null
        };

        if (payload.authenticated) {
          return of(payload).pipe(
            switchMap((_): Observable<User> => fromPromise(this.oauthService.loadUserProfile() as Promise<User>)),
            map((user: User) => {
              payload.user = user;
              this.permissionsService.loadPermissions(withDerivedPermissions(user.roles));
              setSentryUser(user);
              return payload;
            }),
          );
        }

        return of(payload);
      }),
      map((payload) => {
        console.log('AUTH_INIT_ACTION', payload);
        return new AuthActions.Init(payload);
      })
    ));

  authLogin = createEffect(() => this.actions$
    .pipe(
      ofType(AuthActions.LOGIN),
      map((action: AuthActions.Login) => action.payload),
      switchMap((authData: {username: string, password: string}) => {
        return from(this.oauthService.fetchTokenUsingPasswordFlowAndLoadUserProfile(authData.username, authData.password))
          .pipe(
            map((user: User) => {
              return new AuthActions.LoginSuccess(user);
            }),
            catchError((error: HttpErrorResponse) => {
              const errorMessage = extractHttpErrorMessage(error);
              return of(new AuthActions.LoginFailure(errorMessage));
            }),
          );
      })
    ));

  authLoginSuccess = createEffect(() => this.actions$
    .pipe(
      ofType(AuthActions.LOGIN_SUCCESS),
      tap((action) => console.log(action)),
      map((action: AuthActions.LoginSuccess) => action.payload),
      tap(user => this.permissionsService.loadPermissions(withDerivedPermissions(user.roles))),
      tap(user => setSentryUser(user)),
      tap(() => this.router.navigate(['/']))
    ), {dispatch: false});

  authLogout = createEffect(() => this.actions$
    .pipe(
      ofType(AuthActions.LOGOUT),
      tap(() => {
        clearSentryUser();
        this.oauthService.logOut(true);
        this.router.navigate(['/login']);
      })
    ), {dispatch: false});

  constructor(
    private actions$: Actions,
    private oauthService: OAuthService,
    private router: Router,
    private permissionsService: NgxPermissionsService
  ) { }
}
